import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject } from 'rxjs';

import { EmployeeProfileService } from './employee-profile.service';
import { SpAuthService } from './utility-services/auth.service';
import { concatMap, filter, map, take } from 'rxjs/operators';

import { UserContext, rawUserContext } from '../models/user-context.interface';
import { ApiCreds } from '../models/profile.interface';
import * as utils from './utils/service.utils';
import { partnerCodeThemeUrl, userContextUrl } from './constants/urls';
import { environment } from 'environments/environment';
import { AppTheme } from '@core/models/theming.interface';

@Injectable({ providedIn: 'root' })
export class UserContextService {
  /*** Observable cache of clean UserContextData */
  userContext$ = new ReplaySubject<UserContext>(1);
  private userContextData$: UserContext;

  constructor(
    private auth: SpAuthService,
    private http: HttpClient,
    private employeeProfileService: EmployeeProfileService
  ) {
    if (
      //identifying if the user is coming here after logging in
      // if not take them to login
      (!document.cookie || !this.isDocumentCookieValid(document.cookie)) &&
      environment.name !== 'development'
    ) {
      document.location.href = window.location.origin + '/SPF/Login/Auth1.aspx';
    } else {
      console.log('window location Origin is : ' + window.location.origin);
      console.log('window location Hostname is : ' + window.location.hostname);
      console.log('2. In Constructor of UserContext Service...');
      this.refresh();
    }
  }

  isDocumentCookieValid(cookie: string): boolean {
    const phrase = 'DisplayName';
    return cookie.indexOf(phrase) > -1;
  }

  /**
   * Fetch Data from API
   * 1. Build URL string.
   * ___
   *
   * 2. Fetch URL string and create Observable with Data.
   * ___
   */
  private fetchData(apiCreds: ApiCreds): Observable<rawUserContext> {
    //* 1.
    const url = `${userContextUrl}/${apiCreds.bcl}/${apiCreds.id}/${location.hostname}`;
    //* 2.
    return this.http.get<rawUserContext>(url);
  }

  private fetchThemeData(programCode: string, partnerCode: string): Observable<AppTheme> {
    this.assignProgramPartnerCodesToContext(programCode, partnerCode);
    //* 1.
    const url = `${partnerCodeThemeUrl}/${programCode}/${partnerCode}`;
    //* 2.
    return this.http.get<AppTheme>(url);
  }

  private assignProgramPartnerCodesToContext(programCode: string, partnerCode: string): void {
    this.userContextData$.programCode = programCode.trim();
    this.userContextData$.partnerCode = partnerCode.trim();
  }

  /**
   * Rename raw API property names into Service-able property names
   * 1. Extract usable UserContext data from API response
   * ___
   * 2. Rename filtered User Context Data with CamelCase.
   * ___
   * @param {rawUserContext} rawUserContext - raw data coming from Employee Service
   */
  private formatUserContext(rawUserContext: rawUserContext): UserContext {
    //* 1.
    const { success, errorMessage, ...filteredData } = rawUserContext;
    //* 2.
    return utils.keysToCamelCase(filteredData);
  }

  private formatUserContextForTheme(themeData: AppTheme): UserContext {
    const { success, errorMessage, ...filteredThemeData } = themeData;
    this.userContextData$.theme = utils.keysToCamelCase(filteredThemeData);
    return this.userContextData$;
  }

  /**
   * Refresh Data from fetch using AuthId's
   * 1. Subscribe to @class {SPAuthService}, a wrapper of @class {Auth0Service}.
   * ___
   * 2. Check Auth0 Authenticated status. If True @returns {#b}. Else @returns {#a}.
   * ___
   * a. False: Call Auth0 @function loginWithRedirect to redirect to Login.
   * ___
   * b. True: Subscribe to @param {AuthId} to fetch & store into UserContextData$.
   */
  refresh(): void {
    /**
     * Refresh data for UserContext
     * 1. Fetch data using ApiCreds passed from EmployeeProfileService.
     * ___
     */
    const fetchAndFormatThemeData = (userContextData: UserContext): void => {
      this.userContextData$ = userContextData;
      this.auth.authIds$
        .pipe(
          take(1),
          concatMap(authIds => this.fetchThemeData(authIds.programCode, authIds.partnerCode)),
          map(themeData => this.formatUserContextForTheme(themeData))
        )
        .subscribe(userContextData => {
          this.userContext$.next(userContextData);
        });
    };

    const fetchUserContext = (): void => {
      this.employeeProfileService.apiCreds$
        //* 1.
        .pipe(
          filter(apiCreds => !!apiCreds),
          take(1),
          //* 2
          concatMap(apiCreds => this.fetchData(apiCreds)),
          //* 3
          map(rawUserContext => this.formatUserContext(rawUserContext))
        )
        .subscribe(UserContextData => fetchAndFormatThemeData(UserContextData));
    };
    //*1.
    this.auth.isAuthenticated$.pipe(take(1)).subscribe(isAuth => {
      if (isAuth) {
        fetchUserContext();
      } else {
        if (this.auth.isSigninMfaRedirectCallback) {
          this.auth.RetrieveAccessToken();
          this.auth.accessTokenSilently$.pipe(take(1)).subscribe(s => fetchUserContext());
        } else {
          console.log('3. In Subscribe of isAuthenticated$ and going to loginwithredirect...');
          this.auth.loginWithRedirect();
          this.auth.accessTokenSilently$.pipe(take(1)).subscribe(s => fetchUserContext());
        }
      }
    });
  }
}
